import { useMutation, useQuery, type UseQueryOptions } from "@tanstack/react-query";

import {
    ChangeRequestType,
    type DocumentDownloadResponse,
    type FileDownloadInfo,
    type GetChangesResponse,
    type GetContactAddressesResponse,
    type GetProfileResponse,
    type GetProfileV2Response,
    type PostLoginResponse,
    type SharedClient,
} from "@repo/api";
import { readEnv } from "@repo/helpers";
import { usePrimaryAPIClient } from "../context/PrimaryAPIClient";
import { useQueryKeyFactory } from "../context/QueryKeyProvider";

type PortalProfileData = {
    client: SharedClient;
    email?: string | null;
    mobile?: string | null;
};

type IDocumentsProps = {
    options?: UseQueryOptions<DocumentDownloadResponse[]>;
};

export const useDocuments = ({ options }: IDocumentsProps = {}) => {
    const { documentsApi } = usePrimaryAPIClient();
    const { createQueryKey } = useQueryKeyFactory();

    return useQuery<DocumentDownloadResponse[]>({
        queryKey: createQueryKey({ key: ["getDocuments"] }),
        queryFn: () => documentsApi.getUserDocumentsGet(),
        ...options,
    });
};

type IFileDownloadInfoProps = {
    options?: Partial<UseQueryOptions<FileDownloadInfo>>;
};

export const usePrecontractualDocs = ({ options }: IFileDownloadInfoProps = {}) => {
    const { documentsApi } = usePrimaryAPIClient();
    const { createQueryKey } = useQueryKeyFactory();

    return useQuery<FileDownloadInfo>({
        queryKey: createQueryKey({
            key: ["getPrecontractualInfo"],
            includeUserIdentifier: false,
        }),
        queryFn: () => documentsApi.getCurrentDPSPrecontractualInfoGet(),
        ...options,
    });
};

type IChangeRequestsProps<TResult = GetChangesResponse[]> = {
    type?: ChangeRequestType;
    options?: UseQueryOptions<GetChangesResponse[], unknown, TResult>;
};

export const useChangeRequests = <TResult extends unknown = GetChangesResponse[]>({
    type = ChangeRequestType.All,
    options,
}: IChangeRequestsProps<TResult> = {}) => {
    const { changeRequestsApi } = usePrimaryAPIClient();
    const { createQueryKey } = useQueryKeyFactory();

    return useQuery<GetChangesResponse[], unknown, TResult>({
        queryKey: createQueryKey({ key: ["getChangeRequests", type] }),
        queryFn: () => changeRequestsApi.getChangeRequestsGet({ type }),
        ...options,
    });
};

type IGetProfileProps = {
    options?: Partial<UseQueryOptions<GetProfileResponse>>;
};

export const useProfile = ({ options }: IGetProfileProps = {}) => {
    const { profileApi } = usePrimaryAPIClient();
    const { createQueryKey } = useQueryKeyFactory();

    return useQuery<GetProfileResponse>({
        queryKey: createQueryKey({ key: ["getUserProfile"] }),
        queryFn: () => profileApi.getProfileGet(),
        ...options,
    });
};

export const isProfileV2Enabled = () => readEnv("GATSBY_FEATURE_PROFILE_V2_ENABLED") === "true";

export const usePortalProfile = () => {
    const { profileApi } = usePrimaryAPIClient();
    const { createQueryKey } = useQueryKeyFactory();
    const profileV2Enabled = isProfileV2Enabled();

    const profileV1Query = useProfile({
        options: {
            enabled: !profileV2Enabled,
        },
    });

    const profileV2Query = useQuery<GetProfileV2Response>({
        queryKey: createQueryKey({ key: ["getPortalProfileV2"] }),
        queryFn: () => profileApi.getProfileV2Get(),
        enabled: profileV2Enabled,
    });

    if (profileV2Enabled) {
        return {
            data: profileV2Query.data
                ? ({
                    client: profileV2Query.data.client,
                    email: profileV2Query.data.email ?? undefined,
                    mobile: profileV2Query.data.mobile ?? undefined,
                } satisfies PortalProfileData)
                : undefined,
            isLoading: profileV2Query.isLoading,
            isSuccess: profileV2Query.isSuccess,
        };
    }

    return {
        data: profileV1Query.data ? ({ client: profileV1Query.data.client } satisfies PortalProfileData) : undefined,
        isLoading: profileV1Query.isLoading,
        isSuccess: profileV1Query.isSuccess,
    };
};

type IUsePhoneNumbers = {
    options?: Partial<UseQueryOptions<string[]>>;
};

export const usePhoneNumbers = ({ options }: IUsePhoneNumbers = {}) => {
    const { authenticateApi } = usePrimaryAPIClient();
    const { createQueryKey } = useQueryKeyFactory();

    return useQuery<string[]>({
        queryKey: createQueryKey({ key: ["getPhoneNumbers"] }),
        queryFn: () => authenticateApi.getAuthenticationPhoneNumbersGet(),
        ...options,
    });
};

type IContactAdressesProps = {
    options?: Partial<UseQueryOptions<GetContactAddressesResponse>>;
};

export const useContactAdresses = ({ options }: IContactAdressesProps = {}) => {
    const { profileApi } = usePrimaryAPIClient();
    const { createQueryKey } = useQueryKeyFactory();

    return useQuery<GetContactAddressesResponse>({
        queryKey: createQueryKey({ key: ["getContactAdresses"] }),
        queryFn: () => profileApi.getContactAddressesGet(),
        ...options,
    });
};

type IUsePostLogin = {
    options?: Partial<UseQueryOptions<PostLoginResponse>>;
};

export const usePostLogin = ({ options }: IUsePostLogin = {}) => {
    const { postLoginApi } = usePrimaryAPIClient();
    const { createQueryKey } = useQueryKeyFactory();

    return useQuery<PostLoginResponse>({
        queryKey: createQueryKey({ key: ["getPostLogin"] }),
        queryFn: () => postLoginApi.getPostLoginInfoGet(),
        ...options,
    });
};

export const useConfirmContacts = () => {
    const { postLoginApi } = usePrimaryAPIClient();

    return useMutation({
        mutationFn: () => {
            return postLoginApi.confirmContactsPut();
        },
    });
};
